import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Grid, Tabs, Tab, CircularProgress, Alert,
  ToggleButtonGroup, ToggleButton, Divider,
} from '@mui/material';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';

import {
  getLeadsFunnelReport, getLeadsSourcesReport, getLeadsAiVerdictsReport,
  getLeadsScoringReport, getLeadsGeoReport, getLeadsAgingReport,
  getLeadsAssignmentReport, getLeadsProfileCoverageReport,
} from '../../services/api';
import KpiCard from '../../components/reports/KpiCard';
import DistributionBar from '../../components/reports/DistributionBar';
import FunnelChart from '../../components/reports/FunnelChart';
import TableWithBars from '../../components/reports/TableWithBars';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import ReportExportButton from '../../components/reports/ReportExportButton';
import TrendSparkline from '../../components/reports/TrendSparkline';

const fmtPct = (v, digits = 1) => v == null ? '—' : `${(v * 100).toFixed(digits)}%`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString();
const fmtDays = (v) => v == null ? '—' : `${Number(v).toFixed(1)}d`;
const fmtHours = (v) => v == null ? '—' : `${Number(v).toFixed(1)}h`;

function ReportSection({ title, action, children }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function useReport(loader, filters) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await loader(filters);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.title || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [loader, JSON.stringify(filters)]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

// ─── L1 ───
function FunnelCard({ filters }) {
  const { data, loading, error } = useReport(getLeadsFunnelReport, filters);
  const stages = data?.data?.stages || [];
  const conv = data?.data?.conversion || {};
  return (
    <ReportSection
      title="L1 · Lead Lifecycle Funnel"
      action={<ReportExportButton path="/reports/leads/funnel/export.csv" filters={filters} filename="leads-funnel.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}><KpiCard label="New → Qualified" value={fmtPct(conv.newToQualified)} tone="info" /></Grid>
            <Grid item xs={12} sm={4}><KpiCard label="Qualified → Converted" value={fmtPct(conv.qualifiedToConverted)} tone="positive" /></Grid>
            <Grid item xs={12} sm={4}><KpiCard label="Overall conversion" value={fmtPct(conv.overall)} tone="positive" /></Grid>
          </Grid>
          <FunnelChart
            stages={stages.map((s) => ({
              label: s.status,
              count: s.count,
              subLabel: s.avgDaysInStage != null ? `avg ${fmtDays(s.avgDaysInStage)}` : null,
            }))}
          />
        </>
      )}
    </ReportSection>
  );
}

// ─── L2 ───
function SourcesCard({ filters }) {
  const { data, loading, error } = useReport(getLeadsSourcesReport, filters);
  const rows = data?.data?.sources || [];
  return (
    <ReportSection
      title="L2 · Lead Source Attribution"
      action={<ReportExportButton path="/reports/leads/sources/export.csv" filters={filters} filename="leads-sources.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="leads"
          columns={[
            { key: 'source', label: 'Source' },
            { key: 'leads', label: 'Leads', align: 'right' },
            { key: 'qualified', label: 'Qualified', align: 'right', render: (r) => fmtInt(r.qualified) },
            { key: 'converted', label: 'Converted', align: 'right', render: (r) => fmtInt(r.converted) },
            { key: 'qualRate', label: 'Qual %', align: 'right', render: (r) => fmtPct(r.qualRate) },
            { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => fmtPct(r.convRate) },
          ]}
          rows={rows}
        />
      )}
    </ReportSection>
  );
}

// ─── L3 ───
function AiVerdictsCard({ filters }) {
  const [dateField, setDateField] = useState('createdAt');
  const f = useMemo(() => ({ ...filters, dateField }), [filters, dateField]);
  const { data, loading, error } = useReport(getLeadsAiVerdictsReport, f);
  const icp = data?.data?.icpFit || {};
  const partner = data?.data?.partnerCandidates || { byType: {}, total: 0 };
  const fresh = data?.data?.freshness || {};
  return (
    <ReportSection
      title="L3 · AI Verdict Distribution"
      action={
        <ToggleButtonGroup size="small" exclusive value={dateField} onChange={(_, v) => v && setDateField(v)}>
          <ToggleButton value="createdAt">Created</ToggleButton>
          <ToggleButton value="insightsUpdatedAt">Verdict-updated</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>ICP Fit</Typography>
            <DistributionBar
              rows={[
                { label: 'CurrentFit', value: icp.CurrentFit || 0, color: '#16a34a' },
                { label: 'FuturePotential', value: icp.FuturePotential || 0, color: '#3b82f6' },
                { label: 'NotAFit', value: icp.NotAFit || 0, color: '#9ca3af' },
                { label: 'Unknown', value: icp.Unknown || 0, color: '#cbd5e1' },
              ]}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Partner Candidates ({partner.total})</Typography>
            <DistributionBar
              rows={Object.entries(partner.byType || {}).map(([k, v]) => ({ label: k, value: v, color: '#8b5cf6' }))}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Verdict Freshness</Typography>
            <DistributionBar
              rows={[
                { label: 'Fresh (<7d)', value: fresh.fresh || 0, color: '#16a34a' },
                { label: 'Aging (7–30d)', value: fresh.aging || 0, color: '#f59e0b' },
                { label: 'Stale (>30d)', value: fresh.stale || 0, color: '#dc2626' },
                { label: 'No verdict', value: fresh.noVerdict || 0, color: '#9ca3af' },
              ]}
            />
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

// ─── L4 ───
const SCORING_METRICS = [
  { key: 'azotteFit', label: 'AzotteFitScore' },
  { key: 'icpMatch', label: 'IcpMatchPercentage' },
  { key: 'enrichment', label: 'EnrichmentCompleteness' },
];

function ScoringCard({ filters }) {
  const [metric, setMetric] = useState('azotteFit');
  const f = useMemo(() => ({ ...filters, metric }), [filters, metric]);
  const { data, loading, error } = useReport(getLeadsScoringReport, f);
  const buckets = data?.data?.buckets || [];
  const baseline = data?.data?.baselineConvRate || 0;
  return (
    <ReportSection
      title="L4 · Scoring Quality"
      action={
        <Stack direction="row" spacing={1}>
          <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
            {SCORING_METRICS.map((m) => (
              <ToggleButton key={m.key} value={m.key}>{m.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ReportExportButton path="/reports/leads/scoring/export.csv" filters={f} filename={`leads-scoring-${metric}.csv`} />
        </Stack>
      }
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Baseline conversion: <strong>{fmtPct(baseline)}</strong>
          </Typography>
          <TableWithBars
            barColumn="leads"
            columns={[
              { key: 'bucket', label: 'Score range', render: (r) => `${r.min}–${r.max}` },
              { key: 'leads', label: 'Leads', align: 'right' },
              { key: 'qualified', label: 'Qualified', align: 'right' },
              { key: 'converted', label: 'Converted', align: 'right' },
              { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => fmtPct(r.convRate) },
              { key: 'lift', label: 'vs base', align: 'right', render: (r) => baseline === 0 ? '—' : `${(r.convRate - baseline >= 0 ? '+' : '')}${((r.convRate - baseline) * 100).toFixed(1)}pp` },
            ]}
            rows={buckets.map((b, i) => ({ id: i, ...b }))}
          />
        </>
      )}
    </ReportSection>
  );
}

// ─── L5 ───
const GEO_SOURCES = [
  { key: 'address', label: 'Address' },
  { key: 'firstSeen', label: 'First seen' },
  { key: 'lastSeen', label: 'Last seen' },
];

function GeoCard({ filters }) {
  const [geoSource, setGeoSource] = useState('address');
  const f = useMemo(() => ({ ...filters, geoSource }), [filters, geoSource]);
  const { data, loading, error } = useReport(getLeadsGeoReport, f);
  const rows = (data?.data?.countries || []).slice(0, 15);
  return (
    <ReportSection
      title="L5 · Geographic Distribution"
      action={
        <Stack direction="row" spacing={1}>
          <ToggleButtonGroup size="small" exclusive value={geoSource} onChange={(_, v) => v && setGeoSource(v)}>
            {GEO_SOURCES.map((s) => (
              <ToggleButton key={s.key} value={s.key}>{s.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ReportExportButton path="/reports/leads/geo/export.csv" filters={f} filename="leads-geo.csv" />
        </Stack>
      }
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <TableWithBars
          barColumn="leads"
          columns={[
            { key: 'code', label: 'Code', render: (r) => r.code || '—' },
            { key: 'name', label: 'Country', render: (r) => r.name || r.code || '—' },
            { key: 'leads', label: 'Leads', align: 'right' },
            { key: 'qualified', label: 'Qualified', align: 'right' },
            { key: 'converted', label: 'Converted', align: 'right' },
            { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => r.leads === 0 ? '—' : fmtPct((r.converted || 0) / r.leads) },
          ]}
          rows={rows.map((r, i) => ({ id: r.code || i, ...r }))}
        />
      )}
    </ReportSection>
  );
}

// ─── L6 ───
function AgingCard({ filters }) {
  const { data, loading, error } = useReport(getLeadsAgingReport, filters);
  const trend = data?.data?.trend || [];
  const stale = data?.data?.stale || {};
  const median = data?.data?.medianAgeByStatus || {};
  return (
    <ReportSection title="L6 · Lead Velocity & Aging">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>New leads / day</Typography>
            <TrendSparkline data={trend} dataKey="new" height={120} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Stale pipeline (current)</Typography>
            <DistributionBar
              rows={Object.entries(stale).map(([k, v]) => ({ label: k, value: v, color: v > 0 ? '#dc2626' : '#9ca3af' }))}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Median age by status (days)</Typography>
            <DistributionBar
              rows={Object.entries(median).map(([k, v]) => ({ label: k, value: v }))}
              valueFormatter={fmtDays}
            />
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

// ─── L7 ───
function AssignmentCard({ filters }) {
  const { data, loading, error } = useReport(getLeadsAssignmentReport, filters);
  const reps = data?.data?.reps || [];
  const unassigned = data?.data?.unassigned || 0;
  return (
    <ReportSection title="L7 · Assignment Load & Rep Performance">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          {unassigned > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>{unassigned} unassigned leads.</Alert>
          )}
          <TableWithBars
            barColumn="leads"
            columns={[
              { key: 'name', label: 'Rep' },
              { key: 'leads', label: 'Leads', align: 'right' },
              { key: 'qualified', label: 'Qualified', align: 'right' },
              { key: 'converted', label: 'Converted', align: 'right' },
              { key: 'avgFirstTouchHours', label: 'First touch', align: 'right', render: (r) => fmtHours(r.avgFirstTouchHours) },
              { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => r.leads === 0 ? '—' : fmtPct(r.converted / r.leads) },
            ]}
            rows={reps.map((r) => ({ id: r.userId, ...r }))}
          />
        </>
      )}
    </ReportSection>
  );
}

// ─── L8 ───
function ProfileCoverageCard({ filters }) {
  const { data, loading, error } = useReport(getLeadsProfileCoverageReport, filters);
  const tags = (data?.data?.topTags || []).slice(0, 10);
  const fields = data?.data?.fieldCoverage || [];
  return (
    <ReportSection title="L8 · Tag Cloud & Profile-Field Coverage">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Top tags</Typography>
            <DistributionBar
              rows={tags.map((t) => ({ label: t.tag, value: t.count, color: '#3b82f6' }))}
              valueFormatter={fmtInt}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Profile-field coverage</Typography>
            {fields.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Pass <code>profilingTemplateId</code> filter to see field-level coverage.
              </Typography>
            ) : (
              <DistributionBar
                rows={fields.map((f) => ({ label: f.field, value: f.pct }))}
                valueFormatter={(v) => fmtPct(v)}
                max={1}
              />
            )}
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'lifecycle', label: 'Lifecycle (L1)' },
  { key: 'sources', label: 'Sources (L2)' },
  { key: 'ai', label: 'AI Verdicts (L3)' },
  { key: 'scoring', label: 'Scoring (L4)' },
  { key: 'geo', label: 'Geo (L5)' },
  { key: 'aging', label: 'Aging (L6)' },
  { key: 'assignment', label: 'Assignment (L7)' },
  { key: 'profile', label: 'Profile (L8)' },
];

export default function LeadsReportPage() {
  const [filters, setFilters] = useState({ window: '30d', includeArchived: false });
  const [tab, setTab] = useState('overview');

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TrendingUpOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Leads Reports</Typography>
        <Box flex={1} />
        <ReportFilterBar value={filters} onChange={setFilters} includeArchivedToggle />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>

      {tab === 'overview' && (
        <>
          <FunnelCard filters={filters} />
          <AiVerdictsCard filters={filters} />
          <SourcesCard filters={filters} />
        </>
      )}
      {tab === 'lifecycle' && <FunnelCard filters={filters} />}
      {tab === 'sources' && <SourcesCard filters={filters} />}
      {tab === 'ai' && <AiVerdictsCard filters={filters} />}
      {tab === 'scoring' && <ScoringCard filters={filters} />}
      {tab === 'geo' && <GeoCard filters={filters} />}
      {tab === 'aging' && <AgingCard filters={filters} />}
      {tab === 'assignment' && <AssignmentCard filters={filters} />}
      {tab === 'profile' && <ProfileCoverageCard filters={filters} />}
    </Box>
  );
}
